import React, { useState, useEffect, useRef } from 'react';
import API_BASE_URL from '../config/api';
import '../styles/Trades.css';

const HedgeCalculatorWindow = ({ tradeId, positionData, hedgeStrategyId, onClose, onPositionCreated }) => {
  const [calculatorData, setCalculatorData] = useState({
    tradeId: tradeId,
    exchangeName: positionData?.name || '---',
    symbol: positionData?.symbol || '---',
    targetProfit: 10,
    actualPrice: '---',
    bestBid: '---',
    bestAsk: '---',
    firstPositionVolume: positionData?.position?.openingVolumeRaw || '',
    riskToRewardRatio: 3,
    exchangeId: positionData?.exchangeId || null,
    symbolId: positionData?.symbolId || null,
    takerFee: positionData?.takerFee || 0,
    market: (positionData?.contract || '').toUpperCase() === 'SPOT' ? 'spot' : 'perpetual',
    hedgeStrategyId: null,
    status: 'INACTIVE',
    timestampCreated: null,
    timestampLastUpdate: null
  });

  const [positions, setPositions] = useState([]);
  const [openMenuStepId, setOpenMenuStepId] = useState(null);
  const [availablePositions, setAvailablePositions] = useState([]);
  const [selectedPositions, setSelectedPositions] = useState({});
  const [editableVolumeStep1, setEditableVolumeStep1] = useState('');
  const [editableOPriceStep1, setEditableOPriceStep1] = useState('');
  const [useActualPriceForStep1] = useState(true);
  const [editableOPrices, setEditableOPrices] = useState({}); // Stores editable OPrices for all steps
  const intervalRef = useRef(null);
  const menuRef = useRef(null);


  // Initialize target profit based on position status
  useEffect(() => {
    if (positionData?.position?.status === 'open') {
      const profit = Math.abs(positionData.position.actualPnlUsdt || 10);
      setCalculatorData(prev => ({
        ...prev,
        targetProfit: parseFloat(profit.toFixed(2))
      }));
    } else {
      setCalculatorData(prev => ({
        ...prev,
        targetProfit: 20
      }));
    }
  }, [positionData]);

  // Load hedge strategy from DB if exists
  const loadHedgeStrategy = async () => {
    try {
      // Use calculatorData.hedgeStrategyId if available (after saving), otherwise use prop
      const idToLoad = calculatorData.hedgeStrategyId || hedgeStrategyId;

      // Only load if hedgeStrategyId is provided and is a valid ID
      if (idToLoad && idToLoad !== 'new') {
        const response = await fetch(`${API_BASE_URL}/api/hedge-strategies/${idToLoad}`);
        const data = await response.json();

        if (data.success && data.data) {
          const strategy = data.data;

          setCalculatorData(prev => ({
            ...prev,
            hedgeStrategyId: strategy.hedge_strategy_id,
            targetProfit: strategy.net_target_profit,
            riskToRewardRatio: strategy.risk_to_reward_ratio || 3,
            status: strategy.status || 'INACTIVE',
            timestampCreated: strategy.timestamp_created,
            timestampLastUpdate: strategy.timestamp_last_update
          }));

          // Load strategy steps
          const stepsResponse = await fetch(`${API_BASE_URL}/api/hedge-strategies/steps?hedge_strategy_id=${strategy.hedge_strategy_id}`);
          const stepsData = await stepsResponse.json();

          if (stepsData.success && stepsData.data) {
            // Fetch current position details for positions assigned to steps
            const positionIds = stepsData.data
              .filter(step => step.position_id)
              .map(step => step.position_id);

            let positionDataMap = {};
            if (positionIds.length > 0) {
              try {
                const tradeResponse = await fetch(`${API_BASE_URL}/api/trades/${tradeId}`);
                const tradeData = await tradeResponse.json();
                if (tradeData.success && tradeData.data.positions) {
                  tradeData.data.positions.forEach(pos => {
                    positionDataMap[pos.position_id] = {
                      status: pos.status,
                      realized_opening_price: pos.realized_opening_price,
                      realized_opening_volume: pos.realized_opening_volume,
                      realized_closing_price: pos.realized_closing_price
                    };
                  });
                }
              } catch (err) {
                console.error('Error fetching position details:', err);
              }
            }

            const loadedPositions = stepsData.data.map(step => {
              const posData = step.position_id ? positionDataMap[step.position_id] : null;

              // Separate position status and hedge step status
              const positionStatus = posData ? posData.status : null;
              const stepStatus = step.step_status || 'N/A';

              // Separate actual, planned, and calculated values
              // Actual values - from position (only if open/closed)
              let actualOPrice = null;
              let actualCPrice = null;
              let actualVolume = null;

              if (posData && (positionStatus === 'open' || positionStatus === 'closed')) {
                actualOPrice = posData.realized_opening_price || null;
                actualCPrice = posData.realized_closing_price || null;
                actualVolume = posData.realized_opening_volume || null;
              }

              // Planned values - from DB hedge_strategy_steps
              const plannedOPrice = step.oprice;
              const plannedCPrice = step.cprice;
              const plannedVolume = step.volume;

              // Preserve calculated values from current positions array if they exist
              const stepOrder = parseInt(step.step_no);
              const existingPos = positions.find(p => p.order === stepOrder);
              const calculatedOPrice = existingPos?.calculatedOPrice;
              const calculatedCPrice = existingPos?.calculatedCPrice;
              const calculatedVolume = existingPos?.calculatedVolume;

              return {
                order: stepOrder,
                stepId: step.hedge_strategy_step_id,
                positionId: step.position_id || 'N/A',
                positionStatus: positionStatus,
                stepStatus: stepStatus,
                contractType: step.position_type,
                actualOPrice: actualOPrice,
                actualCPrice: actualCPrice,
                actualVolume: actualVolume,
                plannedOPrice: plannedOPrice,
                plannedCPrice: plannedCPrice,
                plannedVolume: plannedVolume,
                calculatedOPrice: calculatedOPrice,
                calculatedCPrice: calculatedCPrice,
                calculatedVolume: calculatedVolume,
                isExisting: step.position_id ? true : false
              };
            });
            setPositions(loadedPositions);

            // Set editableVolumeStep1 if step 1 exists
            const step1 = stepsData.data.find(s => parseInt(s.step_no) === 1);
            if (step1 && step1.volume !== null && step1.volume !== undefined) {
              setEditableVolumeStep1(step1.volume.toString());
            }

            // Clear selected positions when loading from DB
            setSelectedPositions({});
          }
        }
      }
    } catch (error) {
      console.error('Error loading hedge strategy:', error);
    }
  };

  useEffect(() => {
    if (tradeId) {
      loadHedgeStrategy();
      loadAvailablePositions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradeId, hedgeStrategyId]);

  // Initialize positions if empty
  useEffect(() => {
    if (positions.length === 0 && positionData && hedgeStrategyId === 'new') {
      const firstPositionType = positionData?.contract || 'LONG';

      const initialPositions = [];
      for (let i = 1; i <= 8; i++) {
        const contractType = (i % 2 === 1)
          ? firstPositionType
          : (firstPositionType === 'LONG' ? 'SHORT' : 'LONG');

        // For the first position, populate actual values if it's an open or closed position
        let actualOPrice = null;
        let actualCPrice = null;
        let actualVolume = null;

        if (i === 1 && positionData) {
          // Check both possible locations for status
          const status = positionData.status || positionData.position?.status;

          if (status === 'open' || status === 'closed') {
            // Get actual values from the position data
            // Try nested structure first (positionData.position.xxx), then flat structure (positionData.realized_xxx)
            actualOPrice = positionData.position?.openingPriceRaw || positionData.realized_opening_price || null;
            actualCPrice = parseFloat(positionData.position?.closedAt) || positionData.realized_closing_price || null;
            actualVolume = positionData.position?.openingVolumeRaw || positionData.realized_opening_volume || null;
          }
        }

        initialPositions.push({
          order: i,
          stepId: null,
          positionId: i === 1 && positionData?.positionId ? positionData.positionId : 'N/A',
          positionStatus: i === 1 ? (positionData?.status || positionData?.position?.status || null) : null,
          stepStatus: 'N/A',
          contractType: contractType,
          actualOPrice: actualOPrice,
          actualCPrice: actualCPrice,
          actualVolume: actualVolume,
          plannedOPrice: null,
          plannedCPrice: null,
          plannedVolume: null,
          isExisting: i === 1 && positionData?.positionId ? true : false
        });
      }

      setPositions(initialPositions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionData, hedgeStrategyId]);

  // Load available positions (positions not yet assigned to any hedge step)
  const loadAvailablePositions = async () => {
    try {
      // Get all positions for this trade
      const tradeResponse = await fetch(`${API_BASE_URL}/api/trades/${tradeId}`);
      const tradeData = await tradeResponse.json();

      if (!tradeData.success || !tradeData.data.positions) {
        setAvailablePositions([]);
        return;
      }

      const allPositions = tradeData.data.positions;

      // Get all hedge strategies for this trade
      const strategiesResponse = await fetch(`${API_BASE_URL}/api/hedge-strategies?trade_id=${tradeId}`);
      const strategiesData = await strategiesResponse.json();

      if (!strategiesData.success || !strategiesData.data || strategiesData.data.length === 0) {
        // No strategies exist, all positions are available
        setAvailablePositions(allPositions);
        return;
      }

      // Get all steps for all strategies to find which positions are already assigned
      const assignedPositionIds = new Set();

      for (const strategy of strategiesData.data) {
        const stepsResponse = await fetch(`${API_BASE_URL}/api/hedge-strategies/steps?hedge_strategy_id=${strategy.hedge_strategy_id}`);
        const stepsData = await stepsResponse.json();

        if (stepsData.success && stepsData.data) {
          stepsData.data.forEach(step => {
            if (step.position_id) {
              assignedPositionIds.add(step.position_id);
            }
          });
        }
      }

      // Filter out positions that are already assigned
      const available = allPositions.filter(pos => !assignedPositionIds.has(pos.position_id));
      setAvailablePositions(available);

    } catch (error) {
      console.error('Error loading available positions:', error);
      setAvailablePositions([]);
    }
  };

  // Call calculate-hedge API to recalculate hedge structure
  const callCalculateHedgeAPI = async (stepNumber, initialVolume, targetProfit, contractType, oPriceRealized, r2r, fee, existingSteps = null) => {
    try {
      const params = new URLSearchParams({
        X: stepNumber,
        InitialVolume: initialVolume,
        TargetProfit: targetProfit,
        ContractTypeStepX: contractType,
        OPriceStepRealized: oPriceRealized,
        R2R: r2r,
        Fee: fee
      });

      const body = existingSteps ? { steps: existingSteps } : {};

      const response = await fetch(`${API_BASE_URL}/api/calculate-hedge?${params}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (data.success) {
        return data.data.steps;
      } else {
        console.error('Error from calculate-hedge API:', data.error);
        return null;
      }
    } catch (error) {
      console.error('Error calling calculate-hedge API:', error);
      return null;
    }
  };

  // Removed: Old calculateHedgeSteps - now using API only

  // Calculate positions when dependencies change
  useEffect(() => {
    const calculatePositions = () => {
      if (positions.length === 0) return;

      // No need to recalculate - the calculation will happen in the render
    };

    calculatePositions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculatorData.targetProfit, calculatorData.riskToRewardRatio, calculatorData.takerFee,
      calculatorData.actualPrice, editableVolumeStep1, editableOPriceStep1, useActualPriceForStep1]);

  // Removed: Auto-recalculate volumes useEffect - now using API only

  // Fetch orderbook data (actual price, best bid, best ask) periodically
  useEffect(() => {
    const fetchOrderbookData = async () => {
      if (!calculatorData.exchangeId || !calculatorData.symbolId) {
        return;
      }

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/orderbook?exchangeId=${calculatorData.exchangeId}&symbolId=${calculatorData.symbolId}&limit=5&market=${calculatorData.market}`
        );
        const data = await response.json();

        if (data.success && data.data) {
          const actualPrice = data.data.actualPrice || '---';
          const bestBid = data.data.bestBid?.price || '---';
          const bestAsk = data.data.bestAsk?.price || '---';

          setCalculatorData(prev => ({
            ...prev,
            actualPrice: actualPrice,
            bestBid: bestBid,
            bestAsk: bestAsk
          }));
        }
      } catch (error) {
        console.error('Error fetching orderbook data:', error);
      }
    };

    fetchOrderbookData();
    intervalRef.current = setInterval(fetchOrderbookData, 3000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [calculatorData.exchangeId, calculatorData.symbolId, calculatorData.market]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuStepId(null);
      }
    };

    if (openMenuStepId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuStepId]);

  const handleInputChange = (field, value) => {
    // Allow empty string or valid number format (including decimal point)
    if (value === '' || value === '-' || value === '.' || /^-?\d*\.?\d*$/.test(value)) {
      if (field === 'targetProfit' && value !== '' && value !== '-' && value !== '.') {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          value = parseFloat(numValue.toFixed(2));
        }
      }
      setCalculatorData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleOPriceStep1Change = (value) => {
    handleOPriceChange(1, value);
  };

  const handleOPriceChange = async (stepOrder, value) => {
    // Remove spaces (thousands separators) from formatted value
    const cleanValue = value.replace(/\s/g, '');

    if (cleanValue === '' || cleanValue === '-' || cleanValue === '.' || /^-?\d*\.?\d*$/.test(cleanValue)) {
      // Update editableOPrices state
      setEditableOPrices(prev => ({
        ...prev,
        [stepOrder]: cleanValue
      }));

      // If step 1, also update editableOPriceStep1 for backward compatibility
      if (stepOrder === 1) {
        setEditableOPriceStep1(cleanValue);
      }

      // If the value is a valid number, call API to recalculate
      if (cleanValue !== '' && cleanValue !== '-' && cleanValue !== '.' && !isNaN(parseFloat(cleanValue))) {
        const oPriceRealized = parseFloat(cleanValue);

        // Get initial volume (step 1 volume)
        let initialVolume = 1;
        if (editableVolumeStep1) {
          initialVolume = parseFloat(editableVolumeStep1);
        } else if (positionData?.position?.openingVolumeRaw) {
          initialVolume = parseFloat(positionData.position.openingVolumeRaw);
        }

        // Get contract type for this step
        const contractType = positions[stepOrder - 1]?.contractType || 'LONG';

        // Prepare existing steps for API call (if recalculating from step > 1)
        let existingSteps = null;
        if (stepOrder > 1) {
          existingSteps = positions.slice(0, stepOrder - 1).map(pos => ({
            stepNo: pos.order,
            contractType: pos.contractType,
            oPrice: pos.calculatedOPrice || 0,
            cPrice: pos.calculatedCPrice || 0,
            volume: pos.calculatedVolume || initialVolume
          }));
        }

        // Call API
        console.log('=== Calling Calculate Hedge API ===');
        console.log('Step:', stepOrder);
        console.log('InitialVolume:', initialVolume);
        console.log('TargetProfit:', parseFloat(calculatorData.targetProfit) || 10);
        console.log('ContractType:', contractType);
        console.log('OPriceRealized:', oPriceRealized);
        console.log('R2R:', parseFloat(calculatorData.riskToRewardRatio) || 3);
        console.log('Fee:', parseFloat(calculatorData.takerFee) / 100 || 0.0006);
        console.log('ExistingSteps:', existingSteps);

        const apiSteps = await callCalculateHedgeAPI(
          stepOrder,
          initialVolume,
          parseFloat(calculatorData.targetProfit) || 10,
          contractType,
          oPriceRealized,
          parseFloat(calculatorData.riskToRewardRatio) || 3,
          parseFloat(calculatorData.takerFee) / 100 || 0.0006,
          existingSteps
        );

        console.log('=== API Response ===');
        console.log('apiSteps:', apiSteps);

        if (apiSteps) {
          // Update positions with API results
          const newPositions = [...positions];
          for (let i = 0; i < apiSteps.length && i < newPositions.length; i++) {
            newPositions[i].calculatedOPrice = apiSteps[i].oPrice;
            newPositions[i].calculatedCPrice = apiSteps[i].cPrice;
            newPositions[i].calculatedVolume = apiSteps[i].volume;
            newPositions[i].contractType = apiSteps[i].contractType;
          }
          setPositions(newPositions);

          // Update step 1 volume if this was step 1 recalculation
          if (stepOrder === 1 && apiSteps[0]) {
            setEditableVolumeStep1(apiSteps[0].volume.toString());
          }
        }
      }
    }
  };

  const handleVolumeStep1Change = async (value) => {
    // Remove spaces (thousands separators) from formatted value
    const cleanValue = value.replace(/\s/g, '');

    if (cleanValue === '' || /^\d*\.?\d*$/.test(cleanValue)) {
      setEditableVolumeStep1(cleanValue);

      // If valid number and we have an OPrice, recalculate using API
      if (cleanValue !== '' && !isNaN(parseFloat(cleanValue)) && positions.length > 0) {
        const volume = parseFloat(cleanValue);

        // Get OPrice for step 1
        let oPrice = null;
        if (editableOPriceStep1) {
          oPrice = parseFloat(editableOPriceStep1);
        } else if (positions[0]?.calculatedOPrice) {
          oPrice = positions[0].calculatedOPrice;
        } else if (calculatorData.actualPrice !== '---') {
          oPrice = parseFloat(calculatorData.actualPrice);
        }

        if (oPrice) {
          // Call API to recalculate all steps with new volume
          const contractType = positions[0]?.contractType || 'LONG';

          const apiSteps = await callCalculateHedgeAPI(
            1,
            volume,
            parseFloat(calculatorData.targetProfit) || 10,
            contractType,
            oPrice,
            parseFloat(calculatorData.riskToRewardRatio) || 3,
            parseFloat(calculatorData.takerFee) / 100 || 0.0006,
            null
          );

          if (apiSteps) {
            const newPositions = [...positions];
            for (let i = 0; i < apiSteps.length && i < newPositions.length; i++) {
              newPositions[i].calculatedOPrice = apiSteps[i].oPrice;
              newPositions[i].calculatedCPrice = apiSteps[i].cPrice;
              newPositions[i].calculatedVolume = apiSteps[i].volume;
              newPositions[i].contractType = apiSteps[i].contractType;
            }
            setPositions(newPositions);
          }
        }
      }
    }
  };

  // Removed: handleVolumeChange - no longer needed (volumes are read-only for steps 2-8)

  const handlePositionSelection = (stepOrder, positionId) => {
    setSelectedPositions(prev => ({
      ...prev,
      [stepOrder]: positionId
    }));
  };

  const handleCreatePosition = async (step) => {
    try {
      // First, ensure the hedge strategy is saved
      if (!calculatorData.hedgeStrategyId) {
        alert('Please save the hedge strategy first before creating positions.');
        return;
      }

      // Create a new position in the trade
      const positionResponse = await fetch(`${API_BASE_URL}/api/trades/${tradeId}/positions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          exchange_id: calculatorData.exchangeId,
          symbol_id: calculatorData.symbolId,
          contract_type: step.contractType
        })
      });

      const positionData = await positionResponse.json();

      if (!positionData.success) {
        throw new Error(positionData.error || 'Failed to create position');
      }

      const newPositionId = positionData.data.position_id;

      // Get values for this step from stored calculated values or fall back to calculation
      let oPrice, cPrice, volume;
      if (step.calculatedOPrice !== undefined && step.calculatedOPrice !== null &&
          step.calculatedCPrice !== undefined && step.calculatedCPrice !== null &&
          step.calculatedVolume !== undefined && step.calculatedVolume !== null) {
        // Use stored values from API
        oPrice = step.calculatedOPrice;
        cPrice = step.calculatedCPrice;
        volume = step.calculatedVolume;
      } else {
        // No calculated values available - use defaults
        oPrice = null;
        cPrice = null;
        volume = 1;
      }

      // Update the hedge step to reference this position
      const updateStepResponse = await fetch(`${API_BASE_URL}/api/hedge-strategies/steps`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hedge_strategy_id: calculatorData.hedgeStrategyId,
          position_id: newPositionId,
          position_type: step.contractType,
          step_no: step.order.toString(),
          step_status: 'PENDING',
          volume: volume,
          o_price: oPrice,
          c_price: cPrice
        })
      });

      const updateStepData = await updateStepResponse.json();

      if (!updateStepData.success) {
        throw new Error(updateStepData.error || 'Failed to update hedge step');
      }

      alert(`Position created successfully for step ${step.order}`);

      // Notify parent that a position was created
      if (onPositionCreated) {
        onPositionCreated();
      }

      // Reload available positions and hedge strategy to refresh data
      await loadAvailablePositions();
      await loadHedgeStrategy();

    } catch (error) {
      console.error('Error creating position:', error);
      alert(`Error creating position: ${error.message}`);
    }
  };

  const reloadHedgeStrategy = async () => {
    // Reload Planned, Actual, PosId, and Status data from DB
    // Keep calculated values and editable fields unchanged
    await loadHedgeStrategy();

    console.log('Hedge strategy reloaded from database');
  };

  const saveHedgeStrategy = async () => {
    try {
      // Save or update hedge strategy
      let hedgeStrategyId = calculatorData.hedgeStrategyId;
      console.log('Starting save - hedgeStrategyId:', hedgeStrategyId);
      console.log('calculatorData:', calculatorData);

      if (!hedgeStrategyId) {
        // Create new hedge strategy
        const strategyResponse = await fetch(`${API_BASE_URL}/api/hedge-strategies`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            trade_id: tradeId,
            net_target_profit: parseFloat(calculatorData.targetProfit),
            taker_fee: parseFloat(calculatorData.takerFee),
            symbol_id: calculatorData.symbolId,
            exchange_id: calculatorData.exchangeId,
            risk_to_reward_ratio: parseFloat(calculatorData.riskToRewardRatio),
            status: calculatorData.status || 'INACTIVE'
          })
        });

        const strategyData = await strategyResponse.json();
        if (strategyData.success) {
          hedgeStrategyId = strategyData.data.hedge_strategy_id;
          setCalculatorData(prev => ({ ...prev, hedgeStrategyId }));
        } else {
          alert(`Failed to save hedge strategy: ${strategyData.error}`);
          return;
        }
      } else {
        // Update existing hedge strategy
        const strategyResponse = await fetch(`${API_BASE_URL}/api/hedge-strategies/${hedgeStrategyId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            net_target_profit: parseFloat(calculatorData.targetProfit),
            taker_fee: parseFloat(calculatorData.takerFee),
            risk_to_reward_ratio: parseFloat(calculatorData.riskToRewardRatio),
            status: calculatorData.status
          })
        });

        const strategyData = await strategyResponse.json();
        if (!strategyData.success) {
          alert(`Failed to update hedge strategy: ${strategyData.error}`);
          return;
        }
      }

      // Calculate and save strategy steps
      let stepsSuccessCount = 0;
      let stepsErrorCount = 0;

      for (const pos of positions) {
        // Get calculated values from positions array (from Calculated column)
        const calculatedOPrice = pos.calculatedOPrice !== undefined && pos.calculatedOPrice !== null ? pos.calculatedOPrice : null;
        const calculatedCPrice = pos.calculatedCPrice !== undefined && pos.calculatedCPrice !== null ? pos.calculatedCPrice : null;
        const calculatedVolume = pos.calculatedVolume !== undefined && pos.calculatedVolume !== null ? pos.calculatedVolume : null;

        // Validate and parse values
        const volume = calculatedVolume !== null ? parseFloat(calculatedVolume) : NaN;
        const openPrice = calculatedOPrice !== null ? parseFloat(calculatedOPrice) : NaN;
        const closePrice = calculatedCPrice !== null ? parseFloat(calculatedCPrice) : null;

        // Check if required values are valid
        if (isNaN(volume)) {
          console.error(`Step ${pos.order}: Invalid volume`, calculatedVolume);
          stepsErrorCount++;
          continue;
        }

        // Use selected position if available, otherwise use existing position
        let positionIdToSave = null;
        if (pos.positionId !== 'N/A') {
          positionIdToSave = pos.positionId;
        } else if (selectedPositions[pos.order]) {
          positionIdToSave = parseInt(selectedPositions[pos.order]);
        }

        const stepData = {
          hedge_strategy_id: hedgeStrategyId,
          position_id: positionIdToSave,
          position_type: pos.contractType,
          step_no: pos.order.toString(),
          volume: volume,
          o_price: !isNaN(openPrice) ? openPrice : null,
          c_price: closePrice && !isNaN(closePrice) ? closePrice : null
        };

        // Only include step_status when creating a new step (stepId is null)
        if (!pos.stepId) {
          stepData.step_status = pos.stepStatus !== 'N/A' ? pos.stepStatus : 'PENDING';
        }

        console.log(`Saving step ${pos.order}:`, stepData);
        console.log(`  hedge_strategy_id type:`, typeof stepData.hedge_strategy_id, 'value:', stepData.hedge_strategy_id);

        const stepResponse = await fetch(`${API_BASE_URL}/api/hedge-strategies/steps`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(stepData)
        });

        const stepResult = await stepResponse.json();
        if (stepResult.success) {
          console.log(`Step ${pos.order} saved successfully`);
          stepsSuccessCount++;
        } else {
          console.error(`Failed to save step ${pos.order}:`, stepResult.error);
          stepsErrorCount++;
        }
      }

      // Reload strategy from DB to get updated timestamps
      if (hedgeStrategyId) {
        const reloadResponse = await fetch(`${API_BASE_URL}/api/hedge-strategies/${hedgeStrategyId}`);
        const reloadData = await reloadResponse.json();

        if (reloadData.success && reloadData.data) {
          setCalculatorData(prev => ({
            ...prev,
            timestampCreated: reloadData.data.timestamp_created,
            timestampLastUpdate: reloadData.data.timestamp_last_update
          }));
        }
      }

      if (stepsErrorCount > 0) {
        alert(`Hedge strategy saved with ${stepsSuccessCount} steps successful and ${stepsErrorCount} steps failed. Check console for details.`);
      } else {
        alert(`Hedge strategy saved successfully with ${stepsSuccessCount} steps!`);
      }

      // Reload available positions and hedge strategy
      await loadAvailablePositions();
      await loadHedgeStrategy();
    } catch (error) {
      console.error('Error saving hedge strategy:', error);
      alert('Error saving hedge strategy. Please try again.');
    }
  };

  const toggleHedgeStatus = async () => {
    try {
      // Check if hedge strategy exists
      if (!calculatorData.hedgeStrategyId) {
        alert('Please save the hedge strategy first before changing its status.');
        return;
      }

      // Determine new status
      const newStatus = calculatorData.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';

      // Update status in backend
      const response = await fetch(`${API_BASE_URL}/api/hedge-strategies/${calculatorData.hedgeStrategyId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: newStatus
        })
      });

      const data = await response.json();

      if (data.success) {
        // Update local state
        setCalculatorData(prev => ({
          ...prev,
          status: newStatus,
          timestampLastUpdate: data.data.timestamp_last_update
        }));

        console.log(`Hedge strategy status changed to ${newStatus}`);
      } else {
        alert(`Failed to update status: ${data.error}`);
      }
    } catch (error) {
      console.error('Error toggling hedge strategy status:', error);
      alert('Error updating hedge strategy status. Please try again.');
    }
  };

  // Helper function to calculate optimal decimal places based on target digits
  const getOptimalDecimalPlaces = (targetDigits, myNumber, maxDecimalPlaces) => {
    // Convert to number if string
    const numValue = typeof myNumber === 'string' ? parseFloat(myNumber.replace(/\s/g, '')) : myNumber;

    // Handle invalid numbers
    if (isNaN(numValue) || numValue === null || numValue === undefined) {
      return maxDecimalPlaces;
    }

    // Get the integer part length
    const integerPart = Math.floor(Math.abs(numValue));
    const integerDigits = integerPart === 0 ? 1 : Math.floor(Math.log10(integerPart)) + 1;

    // Calculate available digits for decimal places
    // targetDigits - integerDigits - 1 (for decimal point)
    const availableForDecimals = targetDigits - integerDigits - 1;

    // Return the minimum of available decimals and max decimals, but at least 0
    return Math.max(0, Math.min(availableForDecimals, maxDecimalPlaces));
  };

  const formatForDisplay = (value, decimalPlaces = 4) => {
    if (value === 'N/A' || value === '---' || value === null || value === undefined) {
      return value;
    }

    const numValue = typeof value === 'string' ? parseFloat(value.replace(/\s/g, '')) : value;

    if (isNaN(numValue)) {
      return value;
    }

    const fixedValue = numValue.toFixed(decimalPlaces);
    const parts = fixedValue.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1] ? '.' + parts[1] : '';
    const formatted = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    return formatted + decimalPart;
  };

  // Calculate Act.PNL from actual position values
  const calculateActualPnl = (pos) => {
    if (!pos.actualOPrice || !pos.actualCPrice || !pos.actualVolume) {
      return 'N/A';
    }

    const oPrice = parseFloat(pos.actualOPrice);
    const cPrice = parseFloat(pos.actualCPrice);
    const volume = parseFloat(pos.actualVolume);
    const takerFeePercent = parseFloat(calculatorData.takerFee) || 0;
    const takerFeeDecimal = takerFeePercent / 100;

    if (isNaN(oPrice) || isNaN(cPrice) || isNaN(volume)) {
      return 'N/A';
    }

    let pnl;
    if (pos.contractType === 'LONG') {
      pnl = cPrice * (1 - takerFeeDecimal) * volume - oPrice * (1 + takerFeeDecimal) * volume;
    } else {
      pnl = oPrice * (1 - takerFeeDecimal) * volume - cPrice * (1 + takerFeeDecimal) * volume;
    }

    return pnl;
  };

  // Calculate PNL from calculated values (stored from API)
  const calculateCalculatedPnl = (pos) => {
    // Use stored calculated values from API
    if (pos.calculatedOPrice === undefined || pos.calculatedOPrice === null ||
        pos.calculatedCPrice === undefined || pos.calculatedCPrice === null ||
        pos.calculatedVolume === undefined || pos.calculatedVolume === null) {
      return 'N/A';
    }

    const oPrice = pos.calculatedOPrice;
    const cPrice = pos.calculatedCPrice;
    const volume = pos.calculatedVolume;

    const takerFeePercent = parseFloat(calculatorData.takerFee) || 0;
    const takerFeeDecimal = takerFeePercent / 100;

    if (isNaN(oPrice) || isNaN(cPrice) || isNaN(volume)) {
      return 'N/A';
    }

    let pnl;
    if (pos.contractType === 'LONG') {
      pnl = cPrice * (1 - takerFeeDecimal) * volume - oPrice * (1 + takerFeeDecimal) * volume;
    } else {
      pnl = oPrice * (1 - takerFeeDecimal) * volume - cPrice * (1 + takerFeeDecimal) * volume;
    }

    return pnl;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '---';

    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  return (
    <>
      <div className="trade-detail-container" style={{ padding: '8px 10px' }}>

        {/* Title */}
        <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ color: '#ffffff', fontSize: '14px', margin: 0 }}>
            Hedge Manager
          </h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={reloadHedgeStrategy}
              style={{
                backgroundColor: '#30363d',
                color: '#e6edf3',
                border: '1px solid #444c56',
                borderRadius: '6px',
                padding: '4px 12px',
                fontSize: '12px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#444c56';
                e.target.style.borderColor = '#545d68';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#30363d';
                e.target.style.borderColor = '#444c56';
              }}
            >
              Reload
            </button>
            <button
              onClick={saveHedgeStrategy}
              style={{
                backgroundColor: '#238636',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                padding: '4px 12px',
                fontSize: '12px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2ea043'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#238636'}
            >
              Save Strategy
            </button>
          </div>
        </div>

        {/* Trade Info Part */}
        <div style={{ marginBottom: '10px' }}>
          <h4 style={{ color: '#8b949e', fontSize: '12px', marginBottom: '5px', textTransform: 'uppercase' }}>
            Trade Info
          </h4>

          <table className="exchange-details-grid" style={{ width: 'auto', display: 'table', margin: 0 }}>
            <tbody>
              {/* Trade ID */}
              <tr className="exchange-row">
                <td className="exchange-cell label" style={{ whiteSpace: 'nowrap' }}>Trade:</td>
                <td className="exchange-cell value">{calculatorData.tradeId}</td>
              </tr>

              {/* Hedge Strategy ID */}
              <tr className="exchange-row">
                <td className="exchange-cell label" style={{ whiteSpace: 'nowrap' }}>Hedge strategy:</td>
                <td className="exchange-cell value">{calculatorData.hedgeStrategyId || '---'}</td>
              </tr>

              {/* Status */}
              <tr className="exchange-row">
                <td className="exchange-cell label" style={{ whiteSpace: 'nowrap' }}>Status:</td>
                <td className="exchange-cell value">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      color: calculatorData.status === 'ACTIVE' ? '#3fb950' : '#8b949e',
                      fontWeight: 'bold'
                    }}>
                      {calculatorData.status}
                    </span>
                    <button
                      onClick={toggleHedgeStatus}
                      style={{
                        backgroundColor: calculatorData.status === 'ACTIVE' ? '#da3633' : '#238636',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '2px 8px',
                        fontSize: '11px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = calculatorData.status === 'ACTIVE' ? '#e5534b' : '#2ea043';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = calculatorData.status === 'ACTIVE' ? '#da3633' : '#238636';
                      }}
                    >
                      {calculatorData.status === 'ACTIVE' ? 'DEACTIVATE' : 'ACTIVATE'}
                    </button>
                  </div>
                </td>
              </tr>

              {/* Exchange */}
              <tr className="exchange-row">
                <td className="exchange-cell label" style={{ whiteSpace: 'nowrap' }}>Exchange:</td>
                <td className="exchange-cell value">{calculatorData.exchangeName}</td>
              </tr>

              {/* Symbol */}
              <tr className="exchange-row">
                <td className="exchange-cell label" style={{ whiteSpace: 'nowrap' }}>Symbol:</td>
                <td className="exchange-cell value">{calculatorData.symbol}</td>
              </tr>

              {/* Target Profit */}
              <tr className="exchange-row">
                <td className="exchange-cell label" style={{ whiteSpace: 'nowrap' }}>Target profit [USDT]:</td>
                <td className="exchange-cell value">
                  <input
                    type="text"
                    value={calculatorData.targetProfit}
                    onChange={(e) => handleInputChange('targetProfit', e.target.value)}
                    style={{
                      backgroundColor: '#0d1117',
                      border: '1px solid #30363d',
                      borderRadius: '6px',
                      color: '#e6edf3',
                      padding: '2px 5px',
                      margin: 0,
                      width: '90px',
                      fontSize: 'inherit',
                      textAlign: 'right'
                    }}
                  />
                </td>
              </tr>

              {/* Best Bid */}
              <tr className="exchange-row">
                <td className="exchange-cell label" style={{ whiteSpace: 'nowrap' }}>Best bid:</td>
                <td className="exchange-cell value" style={{ color: '#3fb950' }}>
                  {calculatorData.bestBid !== '---'
                    ? formatForDisplay(calculatorData.bestBid, 6)
                    : '---'}
                </td>
              </tr>

              {/* Actual Price */}
              <tr className="exchange-row">
                <td className="exchange-cell label" style={{ whiteSpace: 'nowrap' }}>Actual price:</td>
                <td className="exchange-cell value">
                  {calculatorData.actualPrice !== '---'
                    ? formatForDisplay(calculatorData.actualPrice, 6)
                    : '---'}
                </td>
              </tr>

              {/* Best Ask */}
              <tr className="exchange-row">
                <td className="exchange-cell label" style={{ whiteSpace: 'nowrap' }}>Best ask:</td>
                <td className="exchange-cell value" style={{ color: '#f85149' }}>
                  {calculatorData.bestAsk !== '---'
                    ? formatForDisplay(calculatorData.bestAsk, 6)
                    : '---'}
                </td>
              </tr>

              {/* Risk to Reward Ratio */}
              <tr className="exchange-row">
                <td className="exchange-cell label" style={{ whiteSpace: 'nowrap' }}>Risk to reward ratio:</td>
                <td className="exchange-cell value">
                  <input
                    type="text"
                    value={calculatorData.riskToRewardRatio}
                    onChange={(e) => handleInputChange('riskToRewardRatio', e.target.value)}
                    style={{
                      backgroundColor: '#0d1117',
                      border: '1px solid #30363d',
                      borderRadius: '6px',
                      color: '#e6edf3',
                      padding: '2px 5px',
                      margin: 0,
                      width: '90px',
                      fontSize: 'inherit',
                      textAlign: 'right'
                    }}
                  />
                </td>
              </tr>

              {/* Taker Fee */}
              <tr className="exchange-row">
                <td className="exchange-cell label" style={{ whiteSpace: 'nowrap' }}>Taker fee [%]:</td>
                <td className="exchange-cell value">
                  {calculatorData.takerFee !== null && calculatorData.takerFee !== undefined
                    ? parseFloat(calculatorData.takerFee).toFixed(2)
                    : '---'}
                </td>
              </tr>

              {/* Created Timestamp */}
              <tr className="exchange-row">
                <td className="exchange-cell label" style={{ whiteSpace: 'nowrap' }}>Created:</td>
                <td className="exchange-cell value" style={{ fontSize: '11px', color: '#8b949e' }}>
                  {formatTimestamp(calculatorData.timestampCreated)}
                </td>
              </tr>

              {/* Last Update Timestamp */}
              <tr className="exchange-row">
                <td className="exchange-cell label" style={{ whiteSpace: 'nowrap' }}>Last update:</td>
                <td className="exchange-cell value" style={{ fontSize: '11px', color: '#8b949e' }}>
                  {formatTimestamp(calculatorData.timestampLastUpdate)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Position Calculator Part */}
        <div style={{ marginBottom: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <h4 style={{ color: '#8b949e', fontSize: '12px', margin: 0, textTransform: 'uppercase' }}>
              Position Calculator
            </h4>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="exchange-details-grid" style={{ width: 'auto', display: 'table', margin: 0 }}>
              <thead>
                <tr className="exchange-row" style={{ borderBottom: '1px solid #30363d' }}>
                  <th className="exchange-cell label" style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>No</th>
                  <th className="exchange-cell label" style={{ whiteSpace: 'nowrap' }}>Type</th>
                  <th className="exchange-cell label" style={{ whiteSpace: 'nowrap' }}>Status</th>
                  <th className="exchange-cell label" style={{ whiteSpace: 'nowrap' }}>PosId</th>
                  <th className="exchange-cell label" style={{ whiteSpace: 'pre-line', textAlign: 'center', lineHeight: '1.2' }}>
                    Actual
                  </th>
                  <th className="exchange-cell label" style={{ whiteSpace: 'pre-line', textAlign: 'center', lineHeight: '1.2' }}>
                    Planed
                  </th>
                  <th className="exchange-cell label" style={{ whiteSpace: 'pre-line', textAlign: 'center', lineHeight: '1.2' }}>
                    Calculated
                  </th>
                  <th className="exchange-cell label" style={{ whiteSpace: 'pre-line', textAlign: 'center', lineHeight: '1.2' }}>
                    Act.PNL{'\n'}per step{'\n'}cumul.
                  </th>
                  <th className="exchange-cell label" style={{ whiteSpace: 'pre-line', textAlign: 'center', lineHeight: '1.2' }}>
                    C.PNL{'\n'}per step{'\n'}cumul.
                  </th>
                  <th className="exchange-cell label" style={{ whiteSpace: 'nowrap', width: '30px' }}></th>
                </tr>
              </thead>
              <tbody>
                {positions.map((pos, index) => {
                  const actPnl = calculateActualPnl(pos);
                  const calcPnl = calculateCalculatedPnl(pos, positions);

                  // Use calculated values from positions array if available, otherwise use old calculation method
                  const calculated = {
                    oPrice: pos.calculatedOPrice !== undefined && pos.calculatedOPrice !== null ? pos.calculatedOPrice : 'N/A',
                    cPrice: pos.calculatedCPrice !== undefined && pos.calculatedCPrice !== null ? pos.calculatedCPrice : 'N/A',
                    volume: pos.calculatedVolume !== undefined && pos.calculatedVolume !== null ? pos.calculatedVolume : 'N/A'
                  };

                  // Calculate accumulated PNL for Act.PNL
                  // Odd steps (1,3,5,7) are positive, even steps (2,4,6,8) are negative
                  let accumulatedActPnl = 'N/A';
                  const allActPnls = positions.slice(0, index + 1).map(p => calculateActualPnl(p));

                  if (allActPnls.every(p => p !== 'N/A')) {
                    accumulatedActPnl = 0;
                    for (let i = 0; i <= index; i++) {
                      if (i % 2 === 0) {
                        // Even index = odd step number (1, 3, 5, 7) - add
                        accumulatedActPnl += parseFloat(allActPnls[i]);
                      } else {
                        // Odd index = even step number (2, 4, 6, 8) - subtract
                        accumulatedActPnl -= parseFloat(allActPnls[i]);
                      }
                    }
                  }

                  // Calculate accumulated PNL for C.PNL (calculated)
                  // Odd steps (1,3,5,7) are positive, even steps (2,4,6,8) are negative
                  let accumulatedCalcPnl = 'N/A';
                  const allCalcPnls = positions.slice(0, index + 1).map(p => calculateCalculatedPnl(p, positions));

                  if (allCalcPnls.every(p => p !== 'N/A')) {
                    accumulatedCalcPnl = 0;
                    for (let i = 0; i <= index; i++) {
                      if (i % 2 === 0) {
                        // Even index = odd step number (1, 3, 5, 7) - add
                        accumulatedCalcPnl += parseFloat(allCalcPnls[i]);
                      } else {
                        // Odd index = even step number (2, 4, 6, 8) - subtract
                        accumulatedCalcPnl -= parseFloat(allCalcPnls[i]);
                      }
                    }
                  }

                  // Determine if row should be grey (closed position)
                  const isGreyRow = pos.positionStatus === 'closed';
                  const greyColor = '#6e7681';

                  return (
                    <tr
                      key={`pos-${pos.order}`}
                      className="exchange-row"
                      style={{
                        color: isGreyRow ? greyColor : 'inherit'
                      }}
                    >
                      {/* No */}
                      <td className="exchange-cell value" style={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        color: isGreyRow ? greyColor : 'inherit'
                      }}>
                        {pos.order}
                      </td>

                      {/* Type */}
                      <td className="exchange-cell value" style={{
                        color: isGreyRow ? greyColor :
                               pos.contractType === 'LONG' ? '#3fb950' :
                               pos.contractType === 'SHORT' ? '#f85149' : '#ffffff',
                        whiteSpace: 'nowrap'
                      }}>
                        {pos.contractType}
                      </td>

                      {/* Status - hedge step status */}
                      <td className="exchange-cell value" style={{
                        whiteSpace: 'nowrap',
                        color: isGreyRow ? greyColor : 'inherit'
                      }}>
                        {pos.stepStatus || 'N/A'}
                      </td>

                      {/* PosId - show position ID and position status */}
                      <td className="exchange-cell value" style={{
                        color: isGreyRow ? greyColor : 'inherit',
                        padding: '2px 4px',
                        fontSize: '11px'
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          {pos.order === 1 && hedgeStrategyId === 'new' && pos.positionId !== 'N/A' ? (
                            // Step 1 with new hedge - show ID directly (non-editable)
                            <div>{pos.positionId}</div>
                          ) : pos.positionId === 'N/A' ? (
                            // No position assigned - show dropdown
                            <select
                              value={selectedPositions[pos.order] || ''}
                              onChange={(e) => handlePositionSelection(pos.order, e.target.value)}
                              style={{
                                width: '55px',
                                padding: '1px 2px',
                                margin: '0',
                                backgroundColor: '#0d1117',
                                border: `1px solid ${greyColor}`,
                                borderRadius: '0px',
                                color: isGreyRow ? greyColor : '#c9d1d9',
                                fontSize: '10px',
                                lineHeight: '1',
                                fontWeight: 'inherit',
                                fontFamily: 'inherit'
                              }}
                            >
                              <option value="">---</option>
                              {availablePositions
                                .filter(availPos => availPos.contract_type && availPos.contract_type.toUpperCase() === pos.contractType)
                                .map(availPos => (
                                  <option key={availPos.position_id} value={availPos.position_id}>
                                    {availPos.position_id}
                                  </option>
                                ))}
                            </select>
                          ) : (
                            // Position already assigned - show ID
                            <div>{pos.positionId}</div>
                          )}
                          {/* Show position status below ID */}
                          <div style={{ fontSize: '9px', color: '#8b949e' }}>
                            {pos.positionStatus || '---'}
                          </div>
                        </div>
                      </td>

                      {/* Act. - open price / closing price / volume (from actual position) */}
                      <td className="exchange-cell value" style={{
                        padding: '4px',
                        fontSize: '11px',
                        color: isGreyRow ? greyColor : 'inherit'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                          <span>O:</span>
                          <span>{pos.actualOPrice ? formatForDisplay(pos.actualOPrice, getOptimalDecimalPlaces(8, pos.actualOPrice, 6)) : '---'}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                          <span>C:</span>
                          <span>{pos.actualCPrice ? formatForDisplay(pos.actualCPrice, getOptimalDecimalPlaces(8, pos.actualCPrice, 6)) : '---'}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>V:</span>
                          <span>{pos.actualVolume ? formatForDisplay(pos.actualVolume, getOptimalDecimalPlaces(8, pos.actualVolume, 4)) : '---'}</span>
                        </div>
                      </td>

                      {/* Planed - open price / closing price / volume (from DB hedge_strategy_steps) */}
                      <td className="exchange-cell value" style={{
                        padding: '4px',
                        fontSize: '11px',
                        color: isGreyRow ? greyColor : 'inherit'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                          <span>O:</span>
                          <span>{pos.plannedOPrice ? formatForDisplay(pos.plannedOPrice, getOptimalDecimalPlaces(8, pos.plannedOPrice, 6)) : '---'}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                          <span>C:</span>
                          <span>{pos.plannedCPrice ? formatForDisplay(pos.plannedCPrice, getOptimalDecimalPlaces(8, pos.plannedCPrice, 6)) : '---'}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>V:</span>
                          <span>{pos.plannedVolume ? formatForDisplay(pos.plannedVolume, getOptimalDecimalPlaces(8, pos.plannedVolume, 4)) : '---'}</span>
                        </div>
                      </td>

                      {/* Calc. - open price / closing price / volume (calculated) */}
                      <td className="exchange-cell value" style={{
                        padding: pos.order === 1 ? '1px 4px 0px 4px' : '4px',
                        fontSize: '11px',
                        color: isGreyRow ? greyColor : 'inherit'
                      }}>
                        {pos.order === 1 ? (
                          // Step 1: Show checkbox and input boxes for OPrice and Volume
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>O:</span>
                              <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                                <button
                                  title="copy actual price"
                                  onClick={async () => {
                                    // Check if actual price is available
                                    if (calculatorData.actualPrice === '---' || !calculatorData.actualPrice) {
                                      return;
                                    }

                                    const newPositions = [...positions];

                                    // Sync volumes to positions array
                                    for (let i = 0; i < newPositions.length; i++) {
                                      const stepNum = i + 1;
                                      if (stepNum === 1 && editableVolumeStep1) {
                                        newPositions[i].calculatedVolume = parseFloat(editableVolumeStep1);
                                      } else if (stepNum === 1 && positionData?.position?.openingVolumeRaw) {
                                        newPositions[i].calculatedVolume = parseFloat(positionData.position.openingVolumeRaw);
                                      } else if (!newPositions[i].calculatedVolume) {
                                        newPositions[i].calculatedVolume = 1;
                                      }
                                    }

                                    // Set OPrice for step 1 from actual price and call API
                                    const actualPrice = parseFloat(calculatorData.actualPrice);
                                    setEditableOPriceStep1(calculatorData.actualPrice);

                                    // Get initial volume
                                    let initialVolume = 1;
                                    if (editableVolumeStep1) {
                                      initialVolume = parseFloat(editableVolumeStep1);
                                    } else if (positionData?.position?.openingVolumeRaw) {
                                      initialVolume = parseFloat(positionData.position.openingVolumeRaw);
                                    } else if (newPositions[0]?.calculatedVolume) {
                                      initialVolume = newPositions[0].calculatedVolume;
                                    }

                                    // Get contract type
                                    const contractType = newPositions[0]?.contractType || 'LONG';

                                    // Call API to recalculate
                                    const apiSteps = await callCalculateHedgeAPI(
                                      1,
                                      initialVolume,
                                      parseFloat(calculatorData.targetProfit) || 10,
                                      contractType,
                                      actualPrice,
                                      parseFloat(calculatorData.riskToRewardRatio) || 3,
                                      parseFloat(calculatorData.takerFee) / 100 || 0.0006,
                                      null
                                    );

                                    if (apiSteps) {
                                      for (let i = 0; i < apiSteps.length && i < newPositions.length; i++) {
                                        newPositions[i].calculatedOPrice = apiSteps[i].oPrice;
                                        newPositions[i].calculatedCPrice = apiSteps[i].cPrice;
                                        newPositions[i].calculatedVolume = apiSteps[i].volume;
                                        newPositions[i].contractType = apiSteps[i].contractType;
                                      }
                                      setPositions(newPositions);
                                    }
                                  }}
                                  style={{
                                    width: '14px',
                                    height: '14px',
                                    padding: '0',
                                    margin: '0',
                                    cursor: 'pointer',
                                    backgroundColor: '#0d1117',
                                    border: `1px solid ${greyColor}`,
                                    borderRadius: '2px',
                                    color: '#8b949e',
                                    fontSize: '10px',
                                    lineHeight: '1',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#161b22';
                                    e.target.style.borderColor = '#58a6ff';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = '#0d1117';
                                    e.target.style.borderColor = greyColor;
                                  }}
                                >
                                  ⤓
                                </button>
                                <input
                                  type="text"
                                  value={editableOPriceStep1}
                                  onChange={(e) => handleOPriceStep1Change(e.target.value)}
                                  placeholder="OPrice"
                                  style={{
                                    width: '60px',
                                    padding: '0',
                                    margin: '0',
                                    backgroundColor: '#0d1117',
                                    border: `1px solid ${greyColor}`,
                                    borderRadius: '0px',
                                    color: isGreyRow ? greyColor : 'inherit',
                                    fontSize: '11px',
                                    lineHeight: '1',
                                    fontWeight: 'inherit',
                                    fontFamily: 'inherit',
                                    textAlign: 'right'
                                  }}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontStyle: 'italic' }}>C:</span>
                              <span style={{ fontStyle: 'italic' }}>
                                {calculated.cPrice !== 'N/A' ? formatForDisplay(calculated.cPrice, getOptimalDecimalPlaces(8, calculated.cPrice, 6)) : '---'}
                              </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>V:</span>
                              <input
                                type="text"
                                value={editableVolumeStep1 ||
                                  ((pos.positionStatus === 'open' || pos.positionStatus === 'closed') && positionData?.position?.openingVolumeRaw
                                    ? positionData.position.openingVolumeRaw
                                    : '1')}
                                onChange={(e) => handleVolumeStep1Change(e.target.value)}
                                placeholder="Volume"
                                style={{
                                  width: '60px',
                                  padding: '0',
                                  margin: '0',
                                  backgroundColor: '#0d1117',
                                  border: `1px solid ${greyColor}`,
                                  borderRadius: '0px',
                                  color: isGreyRow ? greyColor : 'inherit',
                                  fontSize: '11px',
                                  lineHeight: '1',
                                  fontWeight: 'inherit',
                                  fontFamily: 'inherit',
                                  textAlign: 'right'
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                          // Other steps: Show OPrice editable, CPrice calculated, Volume editable
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                              <span>O:</span>
                              <input
                                type="text"
                                value={editableOPrices[pos.order] !== undefined && editableOPrices[pos.order] !== ''
                                  ? editableOPrices[pos.order]
                                  : (calculated.oPrice !== 'N/A' ? calculated.oPrice : '')}
                                onChange={(e) => handleOPriceChange(pos.order, e.target.value)}
                                placeholder="OPrice"
                                style={{
                                  width: '60px',
                                  padding: '0',
                                  margin: '0',
                                  backgroundColor: '#0d1117',
                                  border: `1px solid ${greyColor}`,
                                  borderRadius: '0px',
                                  color: isGreyRow ? greyColor : 'inherit',
                                  fontSize: '11px',
                                  lineHeight: '1',
                                  fontWeight: 'inherit',
                                  fontFamily: 'inherit',
                                  textAlign: 'right'
                                }}
                              />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                              <span style={{ fontStyle: 'italic' }}>C:</span>
                              <span style={{ fontStyle: 'italic' }}>
                                {calculated.cPrice !== 'N/A' ? formatForDisplay(calculated.cPrice, getOptimalDecimalPlaces(8, calculated.cPrice, 6)) : '---'}
                              </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontStyle: 'italic' }}>V:</span>
                              <span style={{ fontStyle: 'italic' }}>
                                {calculated.volume !== 'N/A' ? formatForDisplay(calculated.volume, getOptimalDecimalPlaces(8, calculated.volume, 4)) : '---'}
                              </span>
                            </div>
                          </div>
                        )}
                      </td>

                      {/* Act.PNL */}
                      <td className="exchange-cell value" style={{
                        whiteSpace: 'pre-line',
                        textAlign: 'right',
                        lineHeight: '1.4',
                        fontSize: '11px',
                        color: isGreyRow ? greyColor : 'inherit'
                      }}>
                        <span style={{
                          color: pos.positionStatus === 'closed' ? greyColor :
                                 pos.positionStatus === 'pending' || !pos.positionStatus ? '#ffffff' :
                                 actPnl !== 'N/A' && parseFloat(actPnl) >= 0 ? '#3fb950' :
                                 actPnl !== 'N/A' && parseFloat(actPnl) < 0 ? '#f85149' : '#ffffff'
                        }}>
                          {actPnl !== 'N/A' ? formatForDisplay(actPnl, 2) : 'N/A'}
                        </span>
                        {'\n'}
                        <span style={{
                          color: pos.positionStatus === 'closed' ? greyColor :
                                 pos.positionStatus === 'pending' || !pos.positionStatus ? '#ffffff' :
                                 accumulatedActPnl !== 'N/A' && parseFloat(accumulatedActPnl) >= 0 ? '#3fb950' :
                                 accumulatedActPnl !== 'N/A' && parseFloat(accumulatedActPnl) < 0 ? '#f85149' : '#ffffff'
                        }}>
                          ({accumulatedActPnl !== 'N/A' ? formatForDisplay(accumulatedActPnl, 2) : 'N/A'})
                        </span>
                      </td>
                      {/* C.PNL */}
                      <td className="exchange-cell value" style={{
                        whiteSpace: 'pre-line',
                        textAlign: 'right',
                        lineHeight: '1.4',
                        fontSize: '11px',
                        color: isGreyRow ? greyColor : 'inherit'
                      }}>
                        <span style={{
                          color: pos.positionStatus === 'closed' ? greyColor :
                                 pos.positionStatus === 'pending' || !pos.positionStatus ? '#ffffff' :
                                 calcPnl !== 'N/A' && parseFloat(calcPnl) >= 0 ? '#3fb950' :
                                 calcPnl !== 'N/A' && parseFloat(calcPnl) < 0 ? '#f85149' : '#ffffff'
                        }}>
                          {calcPnl !== 'N/A' ? formatForDisplay(calcPnl, 2) : 'N/A'}
                        </span>
                        {'\n'}
                        <span style={{
                          color: pos.positionStatus === 'closed' ? greyColor :
                                 pos.positionStatus === 'pending' || !pos.positionStatus ? '#ffffff' :
                                 accumulatedCalcPnl !== 'N/A' && parseFloat(accumulatedCalcPnl) >= 0 ? '#3fb950' :
                                 accumulatedCalcPnl !== 'N/A' && parseFloat(accumulatedCalcPnl) < 0 ? '#f85149' : '#ffffff'
                        }}>
                          ({accumulatedCalcPnl !== 'N/A' ? formatForDisplay(accumulatedCalcPnl, 2) : 'N/A'})
                        </span>
                      </td>

                      {/* Menu icon */}
                      <td className="exchange-cell value" style={{
                        textAlign: 'center',
                        position: 'relative',
                        color: isGreyRow ? greyColor : 'inherit'
                      }}>
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuStepId(openMenuStepId === pos.order ? null : pos.order);
                            }}
                            style={{
                              cursor: 'pointer',
                              fontSize: '16px',
                              fontWeight: 'bold',
                              padding: '0 8px',
                              userSelect: 'none',
                              color: isGreyRow ? greyColor : '#8b949e'
                            }}
                            onMouseEnter={(e) => e.target.style.color = isGreyRow ? greyColor : '#58a6ff'}
                            onMouseLeave={(e) => e.target.style.color = isGreyRow ? greyColor : '#8b949e'}
                          >
                            ⋮
                          </span>

                          {/* Popup menu */}
                          {openMenuStepId === pos.order && (
                            <div
                              ref={menuRef}
                              style={{
                                position: 'absolute',
                                right: '0',
                                top: '18px',
                                backgroundColor: '#161b22',
                                border: '1px solid #30363d',
                                borderRadius: '3px',
                                minWidth: '120px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                                zIndex: 1000,
                                padding: '2px 0',
                                margin: '0'
                              }}
                            >
                              {/* Create position */}
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (pos.positionId === 'N/A') {
                                    handleCreatePosition(pos);
                                    setOpenMenuStepId(null);
                                  }
                                }}
                                style={{
                                  padding: '4px 8px',
                                  margin: '0',
                                  cursor: pos.positionId === 'N/A' ? 'pointer' : 'not-allowed',
                                  color: pos.positionId === 'N/A' ? '#e6edf3' : '#6e7681',
                                  fontSize: '10px',
                                  textAlign: 'left',
                                  transition: 'background-color 0.1s'
                                }}
                                onMouseEnter={(e) => {
                                  if (pos.positionId === 'N/A') {
                                    e.target.style.backgroundColor = '#21262d';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                }}
                              >
                                Create position
                              </div>

                              {/* Open/Close position */}
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (pos.positionId !== 'N/A' && pos.positionStatus !== 'closed') {
                                    // TODO: Implement open/close position
                                    const action = pos.positionStatus === 'open' ? 'Close' : 'Open';
                                    console.log(`${action} position for step`, pos.order);
                                    setOpenMenuStepId(null);
                                  }
                                }}
                                style={{
                                  padding: '4px 8px',
                                  margin: '0',
                                  cursor: pos.positionId !== 'N/A' && pos.positionStatus !== 'closed' ? 'pointer' : 'not-allowed',
                                  color: pos.positionId !== 'N/A' && pos.positionStatus !== 'closed' ? '#e6edf3' : '#6e7681',
                                  fontSize: '10px',
                                  textAlign: 'left',
                                  transition: 'background-color 0.1s'
                                }}
                                onMouseEnter={(e) => {
                                  if (pos.positionId !== 'N/A' && pos.positionStatus !== 'closed') {
                                    e.target.style.backgroundColor = '#21262d';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                }}
                              >
                                {pos.positionStatus === 'open' ? 'Close position' : 'Open position'}
                              </div>

                              {/* Create position job */}
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (pos.positionId !== 'N/A') {
                                    // TODO: Implement create position job
                                    console.log('Create position job for step', pos.order);
                                    setOpenMenuStepId(null);
                                  }
                                }}
                                style={{
                                  padding: '4px 8px',
                                  margin: '0',
                                  cursor: pos.positionId !== 'N/A' ? 'pointer' : 'not-allowed',
                                  color: pos.positionId !== 'N/A' ? '#e6edf3' : '#6e7681',
                                  fontSize: '10px',
                                  textAlign: 'left',
                                  transition: 'background-color 0.1s'
                                }}
                                onMouseEnter={(e) => {
                                  if (pos.positionId !== 'N/A') {
                                    e.target.style.backgroundColor = '#21262d';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                }}
                              >
                                Create position job
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default HedgeCalculatorWindow;
